import React, { useRef } from "react";
import toast from "react-hot-toast";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { logSecretly } from "../../utils/logSecretly";
import { useAppContext } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { useLocalization } from "../../context/LocalizationContext";
import { StartSessionError, startSession } from "../../apis/qr";
import { UserData } from "../../dtos/patient";

interface MockLoginButtonProps {
  qrToken?: string;
}

/**
 * Dev-only button that directly calls mockStartSession and logs in.
 * Only renders in development mode.
 */
const MockLoginButton: React.FC<MockLoginButtonProps> = ({ qrToken }) => {
  const navigate = useAppNavigate();
  const { setUserData } = useAppContext();
  const { persistSession } = useAuth();
  const { t } = useLocalization();
  const isFetchingRef = useRef(false);

  if (process.env.NODE_ENV !== "development") return null;

  const handleMockLogin = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    // New implementation using mockApi
    try {
      logSecretly("Mock login: Starting session for patient 2...");
      const response = await startSession(qrToken);
      
      // The response from mockStartSession is expected to be UserData
      const userData = response as UserData;
      
      persistSession(userData);
      setUserData(userData);
      
      logSecretly("Mock login successful, navigating to dashboard");
      navigate("/dashboard");
    } catch (err) {
      logSecretly("Mock login failed:", err);
      isFetchingRef.current = false;
      if (err instanceof StartSessionError && err.errorCode === "MT-084") {
        toast.error(t("errors.podAlreadyInUse"));
        return;
      }
      alert("Mock login failed. Check console/logs.");
    }
  };

  return (
    <button
      className="welcome-button primary"
      onClick={handleMockLogin}
      style={{ marginBottom: "15px" }}
    >
      Mock Login (Patient 2)
    </button>
  );
};

export default MockLoginButton;
